// Prod_US credits and check status for the person page.
// Asks the backend on open, every STATUS_POLL_MS while a check is analyzing, when the app comes
// back to the front, and when the native app passes on a push ("native-notification-received").
import { useCallback, useEffect, useRef, useState } from "react";
import { getProdUsMe } from "./prodUsApi";
import { STATUS_POLL_MS } from "./prodUsConfig";
import { keepTokenFresh } from "./prodUsSession";

const BUSY = ["uploading", "analyzing"];

export const useProdUsAccount = (enabled) => {
  const [state, setState] = useState({
    loaded: false,
    credits: 0,
    status: "none",
    reportVersion: 0,
  });
  const lastStatus = useRef(null);
  const [event, setEvent] = useState(null); // "ready" | "failed", shown once by the page

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const me = await getProdUsMe();
      const status = me?.check?.status || "none";
      const previous = lastStatus.current;
      lastStatus.current = status;
      setState((s) => ({
        loaded: true,
        credits: Number(me?.app_user?.credits ?? 0),
        status,
        // Bumped when a new report arrives, so the page reloads "View Recent Report".
        reportVersion:
          BUSY.includes(previous) && status === "ready"
            ? s.reportVersion + 1
            : s.reportVersion,
      }));
      if (
        BUSY.includes(previous) &&
        (status === "ready" || status === "failed")
      )
        setEvent(status);
    } catch {
      setState((s) => ({ ...s, loaded: true }));
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) keepTokenFresh();
    refresh();
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled || !BUSY.includes(state.status)) return undefined;
    const id = window.setInterval(refresh, STATUS_POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, state.status, refresh]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("native-notification-received", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("native-notification-received", refresh);
    };
  }, [enabled, refresh]);

  return { ...state, refresh, event, clearEvent: () => setEvent(null) };
};

export default useProdUsAccount;
